import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useDolarBlue } from '../hooks/useDolarBlue';

const PriceChart = ({ priceHistory, currentPrice, currency = 'USD' }) => {
  const { cotizacion } = useDolarBlue();
  const chartRef = useRef(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, price: '', date: '' });

  // Función para normalizar precios a USD
  const normalizeToUSD = (price, priceCurrency) => {
    if (!price || !cotizacion) return price;
    
    // Si el precio ya está en USD, devolverlo tal como está
    if (priceCurrency === 'USD') return price;
    
    // Si está en ARS, convertir a USD usando dólar blue
    if (priceCurrency === 'ARS') {
      return Math.round(price / cotizacion.venta);
    }
    
    return price;
  };

  // Función para normalizar precios a ARS
  const normalizeToARS = (price, priceCurrency) => {
    if (!price || !cotizacion) return price;
    
    // Si el precio ya está en ARS, devolverlo tal como está
    if (priceCurrency === 'ARS') return price;
    
    // Si está en USD, convertir a ARS usando dólar blue
    if (priceCurrency === 'USD') {
      return Math.round(price * cotizacion.venta);
    }
    
    return price;
  };

  // Generar datos de ejemplo si no hay historial
  const generateMockData = () => {
    const months = ['Oct', 'Nov', 'Dic', 'Ene'];
    const basePrice = currentPrice;
    const variation = basePrice * 0.15; // Variación del 15%
    
    return months.map((month, index) => ({
      month,
      price: basePrice + (Math.random() - 0.5) * variation - (variation * 0.3) + (index * 200),
      currency: currency
    }));
  };

  // Procesar datos del historial con normalización de monedas usando información real de BD
  const processHistoryData = () => {
    if (!priceHistory || priceHistory.length === 0) {
      return generateMockData();
    }
    
    // Si solo hay un punto de datos (precio actual), crear una línea plana
    if (priceHistory.length === 1) {
      const currentPrice = priceHistory[0].price;
      return [
        { month: 'Anterior', price: currentPrice, currency: currency },
        { month: 'Actual', price: currentPrice, currency: currency }
      ];
    }
    
    // Determinar la moneda de referencia (la del precio actual)
    const referenceCurrency = currency;

    // Parsear fechas para calcular días entre cambios
    const parseDate = (dateStr) => {
      if (!dateStr || dateStr === 'Actual' || dateStr.startsWith('Ant')) return null;
      // Formato dd/mm
      const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})$/);
      if (match) {
        const day = parseInt(match[1]);
        const month = parseInt(match[2]) - 1;
        const year = new Date().getFullYear();
        const d = new Date(year, month, day);
        // Si la fecha es futura, asumir año anterior
        if (d > new Date()) d.setFullYear(year - 1);
        return d;
      }
      // Formato yyyy-mm-dd
      const match2 = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match2) return new Date(match2[1], parseInt(match2[2]) - 1, match2[3]);
      return null;
    };
    
    // Normalizar todos los precios a la moneda de referencia
    const normalizedHistory = priceHistory.map((item, index) => {
      let normalizedPrice = item.price;
      const originalCurrency = item.currency || referenceCurrency;
      let wasConverted = false;
      
      if (originalCurrency !== referenceCurrency && cotizacion) {
        if (referenceCurrency === 'USD' && originalCurrency === 'ARS') {
          normalizedPrice = normalizeToUSD(item.price, 'ARS');
          wasConverted = true;
        } else if (referenceCurrency === 'ARS' && originalCurrency === 'USD') {
          normalizedPrice = normalizeToARS(item.price, 'USD');
          wasConverted = true;
        }
      }

      // Calcular días entre este punto y el siguiente (más reciente)
      let daysLabel = '';
      if (index < priceHistory.length - 1) {
        const thisDate = parseDate(item.date);
        const nextDate = parseDate(priceHistory[index + 1]?.date);
        if (thisDate && nextDate) {
          const diffDays = Math.round((nextDate - thisDate) / (1000 * 60 * 60 * 24));
          if (diffDays > 0) daysLabel = `${diffDays}d`;
        }
      }

      const dateLabel = item.date === 'Actual' ? 'Actual' : 
             item.date?.startsWith('Ant') ? item.date.replace('Anterior', 'Ant.') : 
             item.date || '';
      
      return {
        month: dateLabel,
        daysToNext: daysLabel,
        price: normalizedPrice,
        currency: referenceCurrency,
        originalPrice: item.price,
        originalCurrency: originalCurrency,
        wasConverted: wasConverted
      };
    });
    
    return normalizedHistory;
  };

  const data = processHistoryData().filter(d => d.price && d.price > 0);

  // Calcular tendencia
  const firstPrice = data[0]?.price || currentPrice;
  const lastPrice = data[data.length - 1]?.price || currentPrice;
  const trend = lastPrice > firstPrice ? 'up' : lastPrice < firstPrice ? 'down' : 'stable';
  const trendPercentage = firstPrice > 0 ? ((lastPrice - firstPrice) / firstPrice * 100).toFixed(1) : '0.0';

  const renderChart = useCallback(() => {
    if (!chartRef.current || data.length === 0) return;

    const container = chartRef.current;
    const width = container.clientWidth;
    const height = 92;
    const margin = { top: 10, right: 52, bottom: 18, left: 4 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    // Clear previous
    d3.select(container).selectAll('*').remove();

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Scales
    const x = d3.scaleLinear().domain([0, data.length - 1]).range([0, innerW]);
    const y = d3.scaleLinear()
      .domain([d3.min(data, d => d.price) * 0.98, d3.max(data, d => d.price) * 1.02])
      .range([innerH, 0]);

    // Line
    const line = d3.line()
      .x((d, i) => x(i))
      .y(d => y(d.price))
      .curve(d3.curveMonotoneX);

    // Gradient fill
    const gradientId = `price-gradient-${Math.random().toString(36).substr(2, 9)}`;
    const defs = svg.append('defs');
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');
    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#7b8fa8').attr('stop-opacity', 0.15);
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#7b8fa8').attr('stop-opacity', 0);

    // Area
    const area = d3.area()
      .x((d, i) => x(i))
      .y0(innerH)
      .y1(d => y(d.price))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data)
      .attr('d', area)
      .attr('fill', `url(#${gradientId})`);

    // Line path
    g.append('path')
      .datum(data)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', '#7b8fa8')
      .attr('stroke-width', 1.5);

    // Annotation at significant inflection points
    if (data.length > 2) {
      let maxChange = 0;
      let inflectionIdx = -1;
      for (let i = 1; i < data.length; i++) {
        const change = Math.abs(data[i].price - data[i - 1].price) / data[i - 1].price;
        if (change > maxChange && change > 0.05) {
          maxChange = change;
          inflectionIdx = i;
        }
      }
      if (inflectionIdx > 0) {
        const d = data[inflectionIdx];
        const cx = x(inflectionIdx);
        const cy = y(d.price);
        const isDown = d.price < data[inflectionIdx - 1].price;
        // Halo circle
        g.append('circle')
          .attr('cx', cx).attr('cy', cy)
          .attr('r', 4)
          .attr('fill', 'none')
          .attr('stroke', '#111110')
          .attr('stroke-width', 2);
        g.append('circle')
          .attr('cx', cx).attr('cy', cy)
          .attr('r', 3)
          .attr('fill', isDown ? '#c75d5d' : '#48a877')
          .attr('opacity', 0.8);
        // Small annotation text
        const pctChange = (maxChange * 100).toFixed(0);
        g.append('text')
          .attr('x', cx)
          .attr('y', cy + (isDown ? 12 : -6))
          .attr('text-anchor', 'middle')
          .attr('fill', '#8a8577')
          .attr('font-size', '8px')
          .text(`${isDown ? '-' : '+'}${pctChange}%`);
      }
    }

    // Direct label at last data point
    const lastIdx = data.length - 1;
    const lastX = x(lastIdx);
    const lastY = y(data[lastIdx].price);
    g.append('circle')
      .attr('cx', lastX).attr('cy', lastY)
      .attr('r', 3)
      .attr('fill', '#7b8fa8');
    
    const formattedPrice = data[lastIdx].price.toLocaleString('es-AR');
    const priceLabel = currency === 'ARS' ? `$${formattedPrice}` : `US$${formattedPrice}`;
    // Position label below if near top, above if near bottom
    const labelY = lastY < innerH * 0.3 ? lastY + 14 : lastY - 6;
    g.append('text')
      .attr('x', lastX + 5)
      .attr('y', labelY)
      .attr('fill', '#8a8577')
      .attr('font-size', '9px')
      .style('font-feature-settings', '"tnum"')
      .style('font-family', '"JetBrains Mono", monospace')
      .text(priceLabel);

    // X-axis labels (first and last only for compact view)
    if (data.length >= 2) {
      g.append('text')
        .attr('x', x(0)).attr('y', innerH + 12)
        .attr('fill', '#6b7280')
        .attr('font-size', '8px')
        .attr('text-anchor', 'start')
        .style('font-feature-settings', '"tnum"')
        .text(data[0].month);
      g.append('text')
        .attr('x', x(lastIdx)).attr('y', innerH + 12)
        .attr('fill', '#6b7280')
        .attr('font-size', '8px')
        .attr('text-anchor', 'end')
        .style('font-feature-settings', '"tnum"')
        .text(data[lastIdx].month);
    }

    // Hover interaction overlay
    const hoverLine = g.append('line')
      .attr('stroke', '#4b5563')
      .attr('stroke-width', 0.5)
      .attr('stroke-dasharray', '2,2')
      .attr('y1', 0).attr('y2', innerH)
      .style('opacity', 0);

    const hoverCircle = g.append('circle')
      .attr('r', 4)
      .attr('fill', '#7b8fa8')
      .attr('stroke', '#1c1c1a')
      .attr('stroke-width', 2)
      .style('opacity', 0);

    const overlay = g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair');

    overlay.on('mousemove', (event) => {
      const [mx] = d3.pointer(event);
      const idx = Math.round(x.invert(mx));
      const clampedIdx = Math.max(0, Math.min(data.length - 1, idx));
      const d = data[clampedIdx];
      const px = x(clampedIdx);
      const py = y(d.price);

      hoverLine.attr('x1', px).attr('x2', px).style('opacity', 1);
      hoverCircle.attr('cx', px).attr('cy', py).style('opacity', 1);

      const formatted = d.price.toLocaleString('es-AR');
      const label = currency === 'ARS' ? `$ ${formatted}` : `US$ ${formatted}`;
      setTooltip({
        show: true,
        x: px + margin.left,
        y: py + margin.top,
        price: label,
        date: d.month
      });
    });

    overlay.on('mouseleave', () => {
      hoverLine.style('opacity', 0);
      hoverCircle.style('opacity', 0);
      setTooltip(t => ({ ...t, show: false }));
    });
  }, [data, currency]);

  useEffect(() => {
    renderChart();
    const ro = new ResizeObserver(() => renderChart());
    if (chartRef.current) ro.observe(chartRef.current);
    return () => ro.disconnect();
  }, [renderChart]);

  return (
    <div className="relative">
      {/* Chart container */}
      <div className="relative" style={{ height: '92px' }}>
        <div ref={chartRef} className="w-full h-full" />
        {/* Gliding tooltip */}
        <div
          className="absolute pointer-events-none px-1.5 py-0.5 rounded text-[9px] bg-[#1c1c1a]/95 border border-[#7b8fa8]/30 text-[#e8e4de] whitespace-nowrap"
          style={{
            left: tooltip.x,
            top: tooltip.y - 24,
            opacity: tooltip.show ? 1 : 0,
            transition: 'left 0.15s ease-out, top 0.15s ease-out, opacity 0.15s',
            fontFeatureSettings: '"tnum"',
          }}
        >
          {tooltip.date && <span className="text-gray-400 mr-1">{tooltip.date}</span>}
          <span>{tooltip.price}</span>
        </div>
      </div>
      
      {/* Indicador de tendencia */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">Evolución de precio</span>
        <div className={`flex items-center space-x-1 ${
          trend === 'up' ? 'text-[#c75d5d]' : 
          trend === 'down' ? 'text-[#48a877]' : 
          'text-[#8a8577]'
        }`}>
          <span className="text-lg">
            {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'}
          </span>
          <span className="font-medium">
            {trend === 'stable' ? 'Sin cambios' : `${Math.abs(trendPercentage)}%`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;
